// Hand-authored editorial case study content for rich /case/:slug pages.
// Keyed by slug. Anything not here falls back to the Sanity stub layout.

export type EditorialCaseStudy = {
  client: string;
  // Title supports one "|accent|" segment that renders as italic-vermilion.
  title: string;
  year: string;
  industry: string;
  offering: string;
  stack: string;
  heroImage?: string;
  /** Optional YouTube video shown as a full-width embed above the hero. Accepts a full YouTube URL or an 11-char video id. */
  heroVideo?: string;
  lede: string;
  stats?: { value: string; label: string }[];

  about: {
    headline: string;
    body: string[];
  };

  challenge: {
    intro: string;
    items: { title: string; body: string }[];
    goal?: string;
  };

  solution: {
    headline: string;
    intro: string;
    principle?: string;
    pillars: { title: string; body: string; bullets?: string[] }[];
  };

  integrations?: { name: string; body: string }[];

  results: { headline: string; title: string; body: string }[];

  proof?: { heading: string; metrics: { label: string; value: string }[] }[];

  highlights?: { title: string; body: string[]; image?: string; imageAlt?: string }[];

  future?: { headline: string; body: string };
};

export const CASE_STUDIES: Record<string, EditorialCaseStudy> = {
  "transforming-digital-experience-of-ironman": {
    client: "The IRONMAN Group",
    title: "Trust, speed, and scale: how IRONMAN built a |repeatable multi-brand DXP| for race-day reliability.",
    year: "2025",
    industry: "Sports & Entertainment",
    offering: "Global sports & events · Multi-brand DXP",
    stack: "Drupal CMS · Acquia Site Studio · Acquia Cloud · Webhook integrations · API integrations",
    heroImage: "/__l5e/assets-v1/99a88cfe-b624-42c6-b442-0ed4f6a63283/ironman-athlete-hero.jpg",
    heroVideo: "https://www.youtube.com/watch?v=1JeEhUG9lws&t=37s",
    lede: "A fractured digital estate became a governed product platform - owned by editors, marketers, and athletes. Brand launches sped up. Editorial trust came back. Race-day stayed on.",
    stats: [
      { value: "170+", label: "Annual events" },
      { value: "200K+", label: "Athletes / yr" },
      { value: "55+", label: "Countries" },
    ],

    about: {
      headline: "The world's largest operator of mass participation sports.",
      body: [
        "The IRONMAN Group runs a global portfolio that spans triathlons, marathons, trail running, road cycling, and multi-sport events - including the iconic IRONMAN® and IRONMAN® 70.3® Triathlon Series, the Rock 'n' Roll® Running Series, IRONKIDS®, the Epic Series™ mountain bike races, and the UTMB® World Series.",
        "Since its first triathlon in 1978, IRONMAN has grown into a worldwide movement, delivering hundreds of events across more than 55 countries. Its 'Anything Is Possible®' ethos inspires millions of athletes - from first-timers to seasoned professionals - to reach extraordinary personal milestones.",
      ],
    },

    challenge: {
      intro:
        "Dozens of brand websites on disconnected systems. Operational inefficiency on one side. A diluted athlete experience on the other.",
      items: [
        {
          title: "Delayed brand launches",
          body: "Each rollout rebuilt components that should have been reusable. Without a repeatable launch framework, engineering teams duplicated effort with every new site and delayed go-to-market readiness.",
        },
        {
          title: "Editorial mistrust and workflow breakdown",
          body: "Editors didn't trust the publishing platform. Fragile integrations could overwrite live edits mid-campaign, forcing manual workarounds and creating friction between technical and editorial stakeholders.",
        },
        {
          title: "Unreliable integrations and inconsistent data",
          body: "Disparate systems caused fragmented data, sync failures, and duplication - eroding reporting accuracy and hindering decisions that depended on real-time event and campaign data.",
        },
        {
          title: "Race-day reliability risks",
          body: "Peak traffic and live media place unique demands on systems. The objective wasn't merely uptime; it was brand protection - accurate event experiences, consistent UX, and reliable content and forms during high-attendance events.",
        },
        {
          title: "Scaling editorial operations",
          body: "The marketing org was expanding rapidly, from ~5–10 editors to 20–25 globally. The platform needed to convert specialist engineering tasks into governed, low-code workflows so teams could publish at scale, with confidence.",
        },
        {
          title: "A foundation for future growth",
          body: "IRONMAN needed a reusable foundation that could support personalization, pro-tier experiences, Coach Finder, and AI-assisted capabilities - turning athlete journeys into marketing segments for lifecycle-driven engagement.",
        },
      ],
      goal: "Build a composable, data-enabled DXP that powers both brand scalability and continuous athlete engagement.",
    },

    solution: {
      headline: "Not standalone sites. A governed |product platform|.",
      intro:
        "We re-architected the platform around measurable business outcomes rather than treating the engagement as a series of technical upgrades. The work was guided by one central principle, and it shaped every decision that followed.",
      principle: "Governance first. Reuse always. Experience always.",
      pillars: [
        {
          title: "Governance-first architecture",
          body: "A structured, decision-driven audit framework classified existing assets as candidates for reuse, refactor, or rebuild - preserving value from prior work while keeping future builds high-quality and maintainable. A centralised Acquia Site Studio component library made consistent brand theming and race-specific customization cheap, drastically reducing engineering effort and accelerating launches.",
        },
        {
          title: "Experience-led discovery and UX strategy",
          body: "Discovery started with the athlete journey, not a page list. A map-first approach clarified what content lives where and how navigation should scale without collapsing.",
          bullets: [
            "What content lives at brand / global level",
            "What content lives at event level",
            "How navigation and page models scale without collapsing",
          ],
        },
        {
          title: "Operational resilience for race-day protection",
          body: "Performance and resilience patterns were aligned with Acquia's reference architecture so the platform held up under peak event windows.",
          bullets: [
            "Acquia Cloud Platform hosting for scalability",
            "Varnish caching for fast edge delivery",
            "Memcache to reduce application load",
            "Deployment validation practices for reliable releases under tight event timelines",
          ],
        },
        {
          title: "Event-driven integrations (no more fragile automation)",
          body: "Fragile cron jobs were replaced with webhook pipelines that fire only on content marked for publishing. The pipelines create drafts and revisions instead of overwriting live edits - preserving editorial intent and removing the most disruptive failure mode of the old workflow. Validation rules, secure key authentication, and detailed error logging made each integration behave predictably and transparently.",
        },
        {
          title: "Structured content models and editorial enablement",
          body: "We standardized entities - athlete profiles, media, event metadata - with governed validation rules. Legacy content, including Hall of Fame and Legends pages, was migrated into Drupal's media library, creating a centralised, future-proof content foundation. Draft / revision workflows and Site Studio theming controls let editors own brand contrast, layouts, and messaging without waiting on developer support.",
        },
        {
          title: "CI/CD automation with shared visibility",
          body: "A repeatable CI/CD process aligned with Acquia deployment workflows kept cadence under real event deadlines. The pipeline was wired into Slack to post real-time build, test, and deployment updates - teams could track progress, respond fast to failures, and stay confident in release readiness.",
        },
      ],
    },

    integrations: [
      {
        name: "Microsoft Dynamics 365",
        body: "Integrated via custom fields on Event and Article nodes to store D365 IDs and modified dates for reference and tracking.",
      },
      {
        name: "TransPerfect",
        body: "Localisation and translation services integrated into Acquia CMS content workflows.",
      },
      {
        name: "HubSpot Forms",
        body: "Lead capture integrated with Acquia CMS forms, newsletter modals, race-connect forms, and landing pages.",
      },
      {
        name: "SendGrid",
        body: "Transactional and notification email delivery integrated with Drupal / Acquia CMS.",
      },
      {
        name: "Google Tag (Analytics)",
        body: "Integrated via module for analytics and tracking across the global estate.",
      },
      {
        name: "Cookie Consent Tool",
        body: "Integrated for compliance and consistent UX across all event brands.",
      },
    ],

    results: [
      {
        headline: "70%",
        title: "Reduction in system load",
        body: "Migrating to webhook-driven integrations reduced the processing burden on API and database systems by more than 70% - improving performance and freeing backend capacity for future scalability.",
      },
      {
        headline: "Record",
        title: "Faster brand launches",
        body: "The Rock 'n' Roll Running Series launched in record time using governed, reusable components. Months of engineering time were saved, setting a new standard for what's possible inside IRONMAN's digital operations.",
      },
      {
        headline: "Zero",
        title: "Downtime on the IRONMAN + ProSeries site merge",
        body: "A multi-brand, multi-system deployment was executed without any global service disruption. UI consistency was preserved through careful component validation and rebuild sequencing - meeting a non-negotiable no-downtime requirement while handling large-scale changes in Site Studio, search indexing, and global navigation.",
      },
      {
        headline: "50–60%",
        title: "Faster time-to-publish",
        body: "Draft / revision workflows restored editorial confidence. Teams could create and publish without fear of their work being overwritten - turning editorial stress into storytelling time.",
      },
      {
        headline: "1×",
        title: "Single source of truth for campaign data",
        body: "Centralised content and enforced validation rules established one source of truth for campaign content and metadata - enabling more accurate reporting, better planning, and fewer production errors during peak seasons.",
      },
      {
        headline: "↓",
        title: "Cost optimization through reuse",
        body: "Selective refactors and disciplined component governance reduced both technical debt and development cost. Fewer bespoke builds meant lower long-term maintenance overhead, all while preserving brand fidelity.",
      },
    ],

    proof: [
      {
        heading: "Epic Series · post vs pre",
        metrics: [
          { label: "Pageviews", value: "+44.1%" },
          { label: "Total users", value: "+52.7%" },
          { label: "Sessions", value: "+72.0%" },
          { label: "Mobile pageviews", value: "+88.9%" },
        ],
      },
      {
        heading: "Rock 'n' Roll · post vs pre",
        metrics: [
          { label: "Pageviews", value: "+8.3%" },
          { label: "Sessions", value: "+29.4%" },
          { label: "Avg. session duration", value: "2m 17s" },
        ],
      },
    ],

    highlights: [
      {
        title: "Structured content models",
        image: "/__l5e/assets-v1/7c5b5907-5b02-43f9-af45-1559251a953e/ironman-highlight-structured-content.jpg",
        imageAlt: "Structured content models diagram",
        body: [
          "One critical challenge was transforming messy, inconsistent API payloads into structured Drupal content. We developed governed content models for entities like Hall of Fame athletes: unique IDs to prevent duplication, legacy media migrated into Drupal's Media library to centralise assets for long-term management, and validation rules to enforce required data fields.",
          "The result was content that was not only accurate, but future-proofed for scaling across additional brands.",
        ],
      },
      {
        title: "Reusable component architecture",
        image: "/__l5e/assets-v1/d23478dd-3031-4be2-8632-9ff08f4e1753/ironman-highlight-reusable-components.jpg",
        imageAlt: "Reusable component architecture diagram",
        body: [
          "Rather than rebuilding each brand site from scratch, we ran a component audit of the IRONMAN platform. Site Studio components, templates, and helpers were selectively reused - speed without sacrificing brand individuality.",
          "Each event site carries unique branding, logos, color palettes, and navigation while sharing a common platform backbone. Reusability audits prevent technical debt from creeping in: weaker components are refactored or rebuilt. The balance between reuse and flexibility produced an enterprise-ready, multi-brand architecture.",
        ],
      },
      {
        title: "Webhook-driven integrations",
        image: "/__l5e/assets-v1/07afd38c-4894-4f38-aa16-b9157395495b/ironman-highlight-webhook-integrations.jpg",
        imageAlt: "Webhook-driven integrations diagram",
        body: [
          "The move from cron jobs to webhook pipelines was one of the most significant shifts. Cron had been causing duplication, overwrites, and unnecessary processing. Now, updates trigger only when content is marked 'Send to Web' - reducing API and database load, and giving editors confidence because webhooks create new drafts or revisions instead of overwriting content.",
          "Secure API-key authentication and detailed error logging gave the integration the resilience and transparency enterprise systems demand - and redefined how integrations work across brands, creating a reusable pattern for future third-party connections.",
        ],
      },
    ],

    future: {
      headline: "Continuous support, continuous elevation.",
      body: "The next chapter is continuous support and continuous development - keeping the site stable, addressing issues as they arise, and elevating the business on the platform as athlete experience, personalization, and AI-assisted capabilities mature.",
    },
  },

};
